import { forwardRef, type ButtonHTMLAttributes, type ForwardedRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/cn';

const painted =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors [&_svg]:size-4 [&_svg]:shrink-0';

export const buttonVariants = cva(
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: `${painted} bg-primary text-primary-foreground shadow-xs hover:bg-primary-hover`,
        secondary: `${painted} bg-secondary text-secondary-foreground hover:bg-muted`,
        destructive: `${painted} bg-destructive text-destructive-foreground shadow-xs hover:opacity-90`,
        outline: `${painted} border border-input bg-background hover:bg-accent hover:text-accent-foreground`,
        ghost: `${painted} hover:bg-accent hover:text-accent-foreground`,
        link: `${painted} text-primary underline-offset-4 hover:underline`,
        // Focus/disabled only — use when the call site already owns paint and layout
        // (tiles, icon-only chrome, marketing tokens). Default size resolves to `auto`.
        unstyled: '',
      },
      size: {
        sm: 'h-8 rounded-md px-3 text-xs',
        default: 'h-9 px-4 py-2',
        lg: 'h-10 rounded-md px-6',
        icon: 'size-9',
        auto: '',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef(function Button(
  { className, variant, size, type = 'button', ...props }: ButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const resolvedSize = variant === 'unstyled' && size == null ? 'auto' : size;
  return (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size: resolvedSize }), className)}
      {...props}
    />
  );
});
